import { keyToLabel } from "@/lib/utils";
import {
  fieldAnchorForChange,
  fieldLabel,
  formatFieldValue,
  isBlank,
  isRecord,
  operationRowName,
  type ItemFieldTarget,
  type ItemHistoryChange,
  type ItemHistoryContext,
} from "./itemHistoryLabels";

/** One line of an event's detail: an entry, a starting value, a text version, or a group heading. */
export type ItemHistoryDetail =
  | ItemHistoryEntryLine
  | { type: "value"; key: string; label: string; value: string }
  | ItemHistoryTextLine
  | { type: "group"; key: string; label: string };

/** An added, removed, or changed entry; one scalar change reads as a changed entry. */
export type ItemHistoryEntryLine =
  | {
      type: "added" | "removed";
      key: string;
      name: string;
      facts: string | null;
      target: ItemFieldTarget | null;
    }
  | {
      type: "changed";
      key: string;
      name: string;
      field: string | null;
      from: string;
      to: string;
      target: ItemFieldTarget | null;
    };

/** Both versions of a long text field. */
export interface ItemHistoryTextLine {
  type: "text";
  key: string;
  label: string;
  from: string | null;
  to: string;
}

/** An event as one sentence: a verb and either an inline entry or a chip that opens the detail. */
export type ItemHistorySentence =
  | {
      type: "entry";
      verb: string;
      line: ItemHistoryEntryLine;
      collection: string | null;
    }
  | {
      type: "chip";
      verb: string;
      lead: string | null;
      chip: string;
      details: ItemHistoryDetail[];
    }
  | { type: "item"; verb: string };

type ItemHistoryRow =
  | { type: "field"; line: Extract<ItemHistoryEntryLine, { type: "changed" }> }
  | { type: "text"; line: ItemHistoryTextLine }
  | { type: "archive"; archived: boolean }
  | { type: "collection"; label: string; details: ItemHistoryEntryLine[] };

type CollectionId =
  | "operations"
  | "components"
  | "upgrades"
  | "margin"
  | "yield"
  | "pricingLines"
  | "files"
  | "vaultFiles";

const COLLECTION_LABELS: Record<CollectionId, string> = {
  operations: "Operations",
  components: "Components",
  upgrades: "Upgrades",
  margin: "Margins",
  yield: "Yield",
  pricingLines: "Pricing Lines",
  files: "Files",
  vaultFiles: "Vault Files",
};
const CREATED_GROUPS: readonly string[] = [
  "Details",
  "Cost Breaks",
  ...Object.values(COLLECTION_LABELS),
];
const TEXT_FIELDS: ReadonlySet<string> = new Set(["description", "briefing"]);
// Old and new values longer than this together read from a chip instead.
const INLINE_CHANGE_MAX_CHARS = 40;

/**
 * An entry's name, with the sub-field it changed when there is one.
 * @param line - The entry line
 * @returns "Deburr · Run Time Per Item", or the name alone
 */
export function entryLabel(line: ItemHistoryEntryLine): string {
  return line.type === "changed" && line.field
    ? `${line.name} · ${line.field}`
    : line.name;
}

/**
 * Turns an event's changes into one sentence. One field or one list entry
 * reads inline; a text edit, a created item, or several changes read as a
 * chip whose detail holds the lines.
 * @param changes - The event's visible changes, in recorded order
 * @param context - The item and the names ids resolve through
 * @param event - Whether the event created the item
 * @returns The sentence the History line renders
 */
export function buildHistorySentence(
  changes: readonly ItemHistoryChange[],
  context: ItemHistoryContext,
  event: { created: boolean },
): ItemHistorySentence {
  if (event.created) {
    const details = createdDetails(changes, context);
    return {
      type: "chip",
      verb: "created",
      lead: "the item",
      chip: countLabel(details),
      details,
    };
  }
  const rows = changedRows(changes, context);
  const single = rows.length === 1 ? singleRowSentence(rows[0]) : null;
  if (single) {
    return single;
  }
  const details = popoverDetails(rows);
  return {
    type: "chip",
    verb: "changed",
    lead: null,
    chip: countLabel(details),
    details,
  };
}

// The sentence one row reads as alone, or null when it needs the chip.
function singleRowSentence(row: ItemHistoryRow): ItemHistorySentence | null {
  if (row.type === "archive") {
    return { type: "item", verb: row.archived ? "archived" : "restored" };
  }
  if (row.type === "field") {
    return entrySentence(row.line, null);
  }
  if (row.type === "text") {
    return {
      type: "chip",
      verb: "edited",
      lead: null,
      chip: row.line.label,
      details: [row.line],
    };
  }
  if (row.details.length !== 1) {
    return null;
  }
  return entrySentence(row.details[0], row.label);
}

// One entry inline, or behind a chip named for it when its old and new values
// are too long to share a line.
function entrySentence(
  line: ItemHistoryEntryLine,
  collection: string | null,
): ItemHistorySentence {
  if (line.type !== "changed") {
    return { type: "entry", verb: line.type, line, collection };
  }
  if (line.from.length + line.to.length > INLINE_CHANGE_MAX_CHARS) {
    return {
      type: "chip",
      verb: "changed",
      lead: null,
      chip: entryLabel(line),
      details: [line],
    };
  }
  return { type: "entry", verb: "changed", line, collection };
}

// Field lines first, then each list's lines, headed when the detail holds
// more than one group.
function popoverDetails(rows: readonly ItemHistoryRow[]): ItemHistoryDetail[] {
  const fieldLines = rows.flatMap((row): ItemHistoryDetail[] => {
    if (row.type === "field" || row.type === "text") {
      return [row.line];
    }
    if (row.type === "archive") {
      return [
        {
          type: "value",
          key: "archivedAt",
          label: "Status",
          value: row.archived ? "Archived" : "Restored",
        },
      ];
    }
    return [];
  });
  const collections = rows.flatMap((row) =>
    row.type === "collection" ? [row] : [],
  );
  const headed = collections.length + (fieldLines.length > 0 ? 1 : 0) > 1;
  return [
    ...fieldLines,
    ...collections.flatMap((row) =>
      headed ? [groupHeading(row.label), ...row.details] : row.details,
    ),
  ];
}

// "12 fields": every line but the group headings.
function countLabel(details: readonly ItemHistoryDetail[]): string {
  const count = details.filter((line) => line.type !== "group").length;
  return `${count} ${count === 1 ? "field" : "fields"}`;
}

function groupHeading(label: string): ItemHistoryDetail {
  return { type: "group", key: `group.${label}`, label };
}

function changedRows(
  changes: readonly ItemHistoryChange[],
  context: ItemHistoryContext,
): ItemHistoryRow[] {
  const collections = new Map<CollectionId, ItemHistoryChange[]>();
  const rows: (ItemHistoryRow | CollectionId)[] = [];
  for (const change of changes) {
    const collection = collectionOf(change.field);
    if (!collection) {
      rows.push(fieldRow(change, context));
      continue;
    }
    if (!collections.has(collection)) {
      rows.push(collection);
    }
    collections.set(collection, [
      ...(collections.get(collection) ?? []),
      change,
    ]);
  }
  return rows.map((row) =>
    typeof row === "string"
      ? {
          type: "collection",
          label: COLLECTION_LABELS[row],
          details: collectionLines(
            row,
            collections.get(row) ?? [],
            context,
            false,
          ),
        }
      : row,
  );
}

function fieldRow(
  change: ItemHistoryChange,
  context: ItemHistoryContext,
): ItemHistoryRow {
  if (change.field === "archivedAt") {
    return { type: "archive", archived: !isBlank(change.to) };
  }
  if (TEXT_FIELDS.has(change.field)) {
    return {
      type: "text",
      line: {
        type: "text",
        key: change.field,
        label: fieldLabel(change.field),
        from: isBlank(change.from) ? null : String(change.from),
        to: formatFieldValue(change.field, change.to, context),
      },
    };
  }
  const target = fieldAnchorForChange(change, context.item);
  return {
    type: "field",
    line: {
      type: "changed",
      key: change.field,
      name: fieldLabel(change.field),
      field: null,
      from: formatFieldValue(change.field, change.from, context),
      to: formatFieldValue(change.field, change.to, context),
      target: target.anchor ? target : null,
    },
  };
}

// One line per added or removed entry, and one per changed sub-field. A
// created item has no old primary drawing, so its primary reads as added.
function collectionLines(
  collection: CollectionId,
  changes: readonly ItemHistoryChange[],
  context: ItemHistoryContext,
  created: boolean,
): ItemHistoryEntryLine[] {
  return changes.map((change) => {
    const { entryKey, subField } = splitEntryKey(collection, change.field);
    const name = entryName(collection, entryKey, change, context);
    const target =
      collection === "operations" || collection === "components"
        ? fieldAnchorForChange(change, context.item)
        : null;
    if (change.field === "files.primary" && created) {
      return {
        type: "added",
        key: change.field,
        name: fileNameOf(change.to, context),
        facts: "primary",
        target,
      };
    }
    if (change.field === "files.primary") {
      return {
        type: "changed",
        key: change.field,
        name: "Primary",
        field: null,
        from: fileNameOf(change.from, context),
        to: fileNameOf(change.to, context),
        target,
      };
    }
    if (isBlank(change.from) && !subField) {
      return {
        type: "added",
        key: change.field,
        name,
        facts: entryFacts(collection, change, context),
        target,
      };
    }
    if (isBlank(change.to) && !subField) {
      return { type: "removed", key: change.field, name, facts: null, target };
    }
    const valueKey = subField ?? change.field;
    return {
      type: "changed",
      key: change.field,
      name,
      field: subField ? keyToLabel(subField) : null,
      from: formatFieldValue(valueKey, change.from, context),
      to: formatFieldValue(valueKey, change.to, context),
      target,
    };
  });
}

// A created item's starting values, grouped under Details, Cost Breaks, and
// each list.
function createdDetails(
  changes: readonly ItemHistoryChange[],
  context: ItemHistoryContext,
): ItemHistoryDetail[] {
  const linesByGroup = new Map<string, ItemHistoryDetail[]>();
  const addLine = (group: string, line: ItemHistoryDetail) =>
    linesByGroup.set(group, [...(linesByGroup.get(group) ?? []), line]);
  const primaryAssetId = primaryAmongAddedAssets(changes);
  for (const change of changes) {
    const collection = collectionOf(change.field);
    if (change.field === "files.primary" && primaryAssetId) {
      continue;
    }
    if (collection) {
      collectionLines(collection, [change], context, true).forEach((line) =>
        addLine(
          COLLECTION_LABELS[collection],
          line.type === "added" && line.key === `files.assets.${primaryAssetId}`
            ? { ...line, facts: "primary" }
            : line,
        ),
      );
      continue;
    }
    addLine(
      change.field.startsWith("costBreaks.") ? "Cost Breaks" : "Details",
      {
        type: "value",
        key: change.field,
        label: fieldLabel(change.field),
        value: formatFieldValue(change.field, change.to, context),
      },
    );
  }
  return CREATED_GROUPS.flatMap((group) => {
    const lines = linesByGroup.get(group);
    return lines ? [groupHeading(group), ...lines] : [];
  });
}

// The created item's primary drawing id, when that drawing is also one of the
// added drawings; its line then carries "primary" instead of a second line.
function primaryAmongAddedAssets(
  changes: readonly ItemHistoryChange[],
): string | null {
  const primaryId = changes.find(
    (change) => change.field === "files.primary",
  )?.to;
  return typeof primaryId === "string" &&
    changes.some((change) => change.field === `files.assets.${primaryId}`)
    ? primaryId
    : null;
}

function collectionOf(field: string): CollectionId | null {
  const [root, second] = field.split(".");
  if (root === "files") {
    return second === "vault" ? "vaultFiles" : "files";
  }
  if (
    root === "operations" ||
    root === "components" ||
    root === "upgrades" ||
    root === "margin" ||
    root === "yield" ||
    root === "pricingLines"
  ) {
    return field.includes(".") ? root : null;
  }
  return null;
}

// The entry a change belongs to, and the sub-field it touches when any.
function splitEntryKey(
  collection: CollectionId,
  field: string,
): { entryKey: string; subField: string | null } {
  const segments = field.split(".");
  if (collection === "margin") {
    return { entryKey: segments.slice(1, 3).join("."), subField: null };
  }
  if (collection === "files" || collection === "vaultFiles") {
    return { entryKey: segments.slice(2).join("."), subField: null };
  }
  const [, entryKey, ...rest] = segments;
  return { entryKey, subField: rest.length > 0 ? rest.join(".") : null };
}

function entryName(
  collection: CollectionId,
  entryKey: string,
  change: ItemHistoryChange,
  context: ItemHistoryContext,
): string {
  switch (collection) {
    case "operations":
      return operationRowName(entryKey, change, context);
    case "components":
      return context.itemNames.get(entryKey) ?? "Component";
    case "upgrades":
      return context.itemNames.get(entryKey) ?? "Upgrade";
    case "margin":
      return marginTierLabel(entryKey);
    case "yield":
      return entryKey === "default" ? "Default" : `${entryKey}+`;
    case "pricingLines":
      return entryKey;
    case "files":
    case "vaultFiles":
      return context.fileNames.get(entryKey) ?? "1 file";
  }
}

// "All, 100+", or "All" for the default tier.
function marginTierLabel(entryKey: string): string {
  const [type, minQty] = entryKey.split(".");
  const scope = keyToLabel(type ?? "");
  return minQty && minQty !== "default" ? `${scope}, ${minQty}+` : scope;
}

// The key facts an added entry shows after its name.
function entryFacts(
  collection: CollectionId,
  change: ItemHistoryChange,
  context: ItemHistoryContext,
): string | null {
  const value = change.to;
  if (collection === "operations") {
    return isRecord(value) ? operationFacts(value) : null;
  }
  if (collection === "components" || collection === "upgrades") {
    return isRecord(value) && value.quantity
      ? `× ${String(value.quantity)}`
      : null;
  }
  if (collection === "margin" || collection === "yield") {
    return formatFieldValue(change.field, value, context);
  }
  return null;
}

// "0.50 h per unit", plus the setup time when it is nonzero.
function operationFacts(row: Record<string, unknown>): string | null {
  const facts = [
    Number(row.runTimePerItem ?? 0)
      ? `${String(row.runTimePerItem)} h per unit`
      : null,
    Number(row.setupTime ?? 0) ? `${String(row.setupTime)} h setup` : null,
  ].filter(Boolean);
  return facts.length > 0 ? facts.join(", ") : null;
}

function fileNameOf(value: unknown, context: ItemHistoryContext): string {
  if (typeof value !== "string") {
    return "None";
  }
  return context.fileNames.get(value) ?? "1 file";
}
